// Shared, kompakte Badges für Energie-Erweiterung Phase E.
// Werden in Dashboard, Leadliste, LeadDetail und ConversionQueue wiederverwendet.
// Keine Logik – nur Lesedarstellung der bestehenden Lead-Felder.

import React from "react";
import { Sun, Thermometer, Zap, Flame, Clock, Sparkles, Wallet, Send, CheckCircle2, Mail, AlertTriangle, Inbox, Timer, MessageSquare, CalendarCheck, Trophy, XCircle, FileText } from "lucide-react";

export const SOLUTION_LABEL = {
  pv: "Solaranlage",
  heat_pump: "Wärmepumpe",
  pv_heat_pump: "Solar + WP",
};

export const SOLUTION_ICON = {
  pv: Sun,
  heat_pump: Thermometer,
  pv_heat_pump: Zap,
};

const SOLUTION_COLORS = {
  pv: "bg-amber-50 border-amber-200 text-amber-800",
  heat_pump: "bg-sky-50 border-sky-200 text-sky-800",
  pv_heat_pump: "bg-violet-50 border-violet-200 text-violet-800",
};

export function SolutionBadge({ value, testid }) {
  const key = value || "pv";
  const Icon = SOLUTION_ICON[key] || Sun;
  const label = SOLUTION_LABEL[key] || "Solaranlage";
  return (
    <span
      data-testid={testid || `solution-badge-${key}`}
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md border text-[10px] font-semibold uppercase tracking-wider ${SOLUTION_COLORS[key] || SOLUTION_COLORS.pv}`}
    >
      <Icon className="w-3 h-3" /> {label}
    </span>
  );
}

const PRIORITY_LABEL = { urgent: "Sofort", high: "Zeitnah", normal: "Standard", low: "Info" };
const PRIORITY_COLORS = {
  urgent: "bg-red-50 border-red-200 text-red-800",
  high: "bg-orange-50 border-orange-200 text-orange-800",
  normal: "bg-slate-50 border-slate-200 text-slate-600",
  low: "bg-slate-50 border-slate-200 text-slate-500",
};

export function PriorityBadge({ value, testid }) {
  const key = value || "normal";
  return (
    <span
      data-testid={testid || `priority-badge-${key}`}
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md border text-[10px] font-semibold uppercase tracking-wider ${PRIORITY_COLORS[key] || PRIORITY_COLORS.normal}`}
    >
      {key === "urgent" && <Flame className="w-3 h-3" />}
      {key === "high" && <Clock className="w-3 h-3" />}
      {PRIORITY_LABEL[key] || key}
    </span>
  );
}

const TEMP_LABEL = { hot: "Hot", warm: "Warm", cold: "Cold", info: "Info" };
const TEMP_COLORS = {
  hot: "bg-orange-50 border-orange-200 text-orange-700",
  warm: "bg-amber-50 border-amber-200 text-amber-800",
  cold: "bg-slate-100 border-slate-200 text-slate-600",
  info: "bg-sky-50 border-sky-200 text-sky-700",
};

export function TemperatureBadge({ value, testid }) {
  if (!value) return null;
  const key = value;
  return (
    <span
      data-testid={testid || `temperature-badge-${key}`}
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md border text-[10px] font-semibold uppercase tracking-wider ${TEMP_COLORS[key] || TEMP_COLORS.cold}`}
    >
      {TEMP_LABEL[key] || key}
    </span>
  );
}

const ESTIMATE_LABEL = {
  "": "—",
  created: "Erstellt",
  sent: "Versendet",
  opened: "Geöffnet",
  send_failed: "Versand fehlgeschlagen",
  estimate_pending: "Ausstehend",
};
const ESTIMATE_COLORS = {
  "": "bg-slate-50 border-slate-200 text-slate-500",
  created: "bg-blue-50 border-blue-200 text-blue-800",
  sent: "bg-emerald-50 border-emerald-200 text-emerald-800",
  opened: "bg-violet-50 border-violet-200 text-violet-800",
  send_failed: "bg-red-50 border-red-200 text-red-800",
  estimate_pending: "bg-amber-50 border-amber-200 text-amber-800",
};

export function EstimateStatusBadge({ value, testid }) {
  if (!value) return null;
  const key = value;
  const label = ESTIMATE_LABEL[key] || key;
  return (
    <span
      data-testid={testid || `estimate-badge-${key}`}
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md border text-[10px] font-semibold uppercase tracking-wider ${ESTIMATE_COLORS[key] || ESTIMATE_COLORS[""]}`}
    >
      {key === "sent" && <Send className="w-3 h-3" />}
      {key === "opened" && <CheckCircle2 className="w-3 h-3" />}
      {key === "created" && <FileText className="w-3 h-3" />}
      {key === "send_failed" && <AlertTriangle className="w-3 h-3" />}
      Preisschätzung: {label}
    </span>
  );
}

// Konsistente Queue-Status-Labels + Farben
export const QUEUE_LABEL = {
  new: "Neu",
  estimate_pending: "Ausstehend",
  estimate_created: "Erstellt",
  estimate_sent: "Versendet",
  send_failed: "Versand fehlgeschlagen",
  opened: "Geöffnet",
  followup_due: "Nachfass fällig",
  replied: "Geantwortet",
  appointment_requested: "Termin angefragt",
  won: "Gewonnen",
  lost: "Verloren",
};

const QUEUE_COLORS = {
  new: "bg-slate-50 border-slate-200 text-slate-700",
  estimate_pending: "bg-amber-50 border-amber-200 text-amber-800",
  estimate_created: "bg-blue-50 border-blue-200 text-blue-800",
  estimate_sent: "bg-emerald-50 border-emerald-200 text-emerald-800",
  send_failed: "bg-red-50 border-red-200 text-red-800",
  opened: "bg-violet-50 border-violet-200 text-violet-800",
  followup_due: "bg-orange-50 border-orange-200 text-orange-800",
  replied: "bg-teal-50 border-teal-200 text-teal-800",
  appointment_requested: "bg-indigo-50 border-indigo-200 text-indigo-800",
  won: "bg-emerald-100 border-emerald-300 text-emerald-900",
  lost: "bg-slate-100 border-slate-300 text-slate-600",
};

const QUEUE_ICON = {
  new: Inbox,
  estimate_pending: Timer,
  estimate_created: FileText,
  estimate_sent: Send,
  send_failed: AlertTriangle,
  opened: CheckCircle2,
  followup_due: Mail,
  replied: MessageSquare,
  appointment_requested: CalendarCheck,
  won: Trophy,
  lost: XCircle,
};

export function QueueStatusBadge({ value, testid }) {
  if (!value) return null;
  const Icon = QUEUE_ICON[value] || Inbox;
  return (
    <span
      data-testid={testid || `queue-badge-${value}`}
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md border text-[10px] font-semibold uppercase tracking-wider ${QUEUE_COLORS[value] || QUEUE_COLORS.new}`}
    >
      <Icon className="w-3 h-3" /> {QUEUE_LABEL[value] || value}
    </span>
  );
}

export function HotLeadBadge({ reason, testid }) {
  return (
    <span
      data-testid={testid || "hot-lead-badge"}
      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md border bg-orange-100 border-orange-300 text-orange-900 text-[10px] font-bold uppercase tracking-wider"
      title={reason || "Sofort-Lead: bitte umgehend melden"}
    >
      <Flame className="w-3 h-3" /> Sofort-Lead
    </span>
  );
}

const TIMELINE_LABEL = {
  immediate: "Sofort",
  under_3_months: "≤ 3 Monate",
  over_3_months: "> 3 Monate",
  info_only: "Info",
};

export function TimelineBadge({ value, testid }) {
  if (!value) return null;
  const label = TIMELINE_LABEL[value] || value;
  return (
    <span
      data-testid={testid || `timeline-badge-${value}`}
      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md border bg-slate-50 border-slate-200 text-slate-600 text-[10px] font-semibold uppercase tracking-wider"
    >
      <Clock className="w-3 h-3" /> {label}
    </span>
  );
}
